import { createFileRoute, redirect, useLoaderData } from '@tanstack/react-router'
import {
  Avatar,
  AvatarFallback,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@repo/ui'
import { Pencil, Settings, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { DeleteProjectDialog } from '@/components/DeleteProjectDialog'
import { ProjectDialog } from '@/components/ProjectDialog'
import { PageContent } from '@/components/PageContent'
import { PageHeader } from '@/components/PageHeader'
import { isConversationWorkspaceAvailable } from '@/features/conversation/availability'

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/')({
  beforeLoad: ({ context, params }) => {
    if (isConversationWorkspaceAvailable(context.user.userOrgProfile?.enabled_features)) {
      throw redirect({
        to: '/$orgSlug/$projectId/workspace',
        params: { orgSlug: params.orgSlug, projectId: params.projectId },
        search: {}
      })
    }
  },
  head: () => ({ meta: [{ title: 'Workspace Home - Snowvault' }] }),
  component: RouteComponent
})

function RouteComponent() {
  const { projectId, orgSlug } = Route.useParams()
  const { allProjects, orgUsers } = useLoaderData({ from: '/_auth/$orgSlug' })
  const project = allProjects.find((p) => p.projectId === parseInt(projectId, 10))
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  if (!project) {
    return (
      <>
        <PageHeader breadcrumbs={[{ label: 'Workspace Not Found' }]} />
        <PageContent>
          <p className="text-muted-foreground">Could not find a workspace with ID {projectId}.</p>
        </PageContent>
      </>
    )
  }

  const createdOn = new Date(project.createdOnTsMs).toLocaleDateString()
  const updatedOn = new Date(project.updatedOnTsMs).toLocaleDateString()

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: `Workspace: ${project.title}` }]}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit Workspace
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setDeleteOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Workspace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <PageContent>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {project.description && <p className="mb-4">{project.description}</p>}
              <p className="text-xs text-muted-foreground">Created: {createdOn}</p>
              <p className="text-xs text-muted-foreground">Last updated: {updatedOn}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Members ({project.members.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {project.members.map((member) => (
                  <li key={member.userId} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {member.firstName[0] || ''}
                        {member.surname[0] || ''}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-sm">
                      <p className="font-medium">
                        {member.firstName} {member.surname}
                      </p>
                      <p className="text-muted-foreground">{member.email}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {editOpen && (
          <ProjectDialog
            mode="edit"
            open={editOpen}
            onClose={() => setEditOpen(false)}
            project={project}
            orgUsers={orgUsers}
          />
        )}
        {deleteOpen && (
          <DeleteProjectDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            projectId={project.projectId}
            projectTitle={project.title}
            orgSlug={orgSlug}
          />
        )}
      </PageContent>
    </>
  )
}
